import React from 'react'
import { datalayerHandler } from '../../../../utils/datalayer'
import { Image } from '../../../../utils/image'
import { Link } from '../../../../utils/link'
import { ctaBlockClasses } from '../../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode, unEntity } from '../../../../utils/utils'
import { CtaBlock } from '../blocksState'
import { useInView } from '../../../hooks/useInView'

export type CtaBlockStyle = Partial<{
  blockContentWidthClassName: string
  blockFullWidthClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  contentClassName: string
  textClassName: string
  footerClassName: string
  visualClassName: string
  imageClassName: string
  pictureClassName: string
  clickableWrapperClassName: string
  clickablePrimaryClassName: string
  clickableSecondaryClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  iconPrimaryId: string
  iconSecondaryId: string
}>

export const CtaBlockLayout = (props: CtaBlock) => {
  const { displayOption, image, inview, index, intro, isLinkType, primaryAction, secondaryAction, title } = props
  const classes = ctaBlockClasses(props.image !== undefined)

  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("CtaBlockLayout inView:", inview),
    !inview?.triggerOnce
  )

  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(
        classes,
        displayOption === 'content-width' ? 'blockContentWidthClassName' : 'blockFullWidthClassName',
        displayOption === 'content-width' ? 'container py-5' : 'container-fluid bg-light my-5'
      ) + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')}
    >
      <div
        className={getClassName(
          classes,
          'innerClassName',
          `d-md-flex align-items-center ${displayOption === 'content-width' ? 'bg-light' : 'container'}`
        )}
      >
        <div className={getClassName(classes, 'contentClassName', 'p-5 flex-grow-1')}>
          {title && (
            <header className={getClassName(classes, 'headerClassName', '')}>
              <h2 className={getClassName(classes, 'titleClassName', 'text-center mb-3')}>
                <span id={`${index}`} aria-hidden="true" className="block__anchor" />
                {typeof title === 'string' ? htmlDecode(title) : title}
              </h2>
            </header>
          )}

          {intro && <p className={getClassName(classes, 'textClassName', 'lead text-center')}>{unEntity(intro)}</p>}

          {((primaryAction && primaryAction.title) || (secondaryAction && secondaryAction.title)) && (
            <footer className={getClassName(classes, 'footerClassName', 'text-center')}>
              {primaryAction && primaryAction.title && (
                <div className={getClassName(classes, 'clickableWrapperClassName', 'd-inline-block')}>
                  <Link
                    title={primaryAction.title}
                    url={primaryAction.url}
                    className={
                      !isLinkType
                        ? getClassName(classes, 'clickablePrimaryClassName', 'btn btn-dark mr-2 d-inline-flex')
                        : getClassName(classes, 'clickableSecondaryClassName', 'btn btn-link px-0 mr-2 d-inline-flex')
                    }
                    additionalOnClick={() =>
                      datalayerHandler('cta', undefined, undefined, {
                        kind: 'link',
                        title: convertReactNodeToString(title),
                        link: primaryAction.url,
                        label: primaryAction.title,
                      })
                    }
                  >
                    <span className={getClassName(classes, 'clickableLabelClassName', '')}>
                      {htmlDecode(primaryAction.title)}
                    </span>
                    {primaryAction.SVGIcon && (
                      <svg
                        className={getClassName(classes, 'clickableIconClassName', 'ml-3')}
                        aria-hidden="true"
                        width={primaryAction.SVGIcon.width ? primaryAction.SVGIcon.width : '24px'}
                        height={primaryAction.SVGIcon.height ? primaryAction.SVGIcon.height : '24px'}
                      >
                        <use
                          xlinkHref={getClassName(
                            classes,
                            !isLinkType ? 'iconPrimaryId' : 'iconSecondaryId',
                            '#icon--chevron-right-large'
                          )}
                        />
                      </svg>
                    )}
                  </Link>
                </div>
              )}

              {secondaryAction && secondaryAction.title && (
                <div className={getClassName(classes, 'clickableWrapperClassName', 'd-inline-block')}>
                  <Link
                    url={secondaryAction.url}
                    title={secondaryAction.title}
                    className={getClassName(
                      classes,
                      'clickableSecondaryClassName',
                      'btn btn-link px-0 mr-2 d-inline-flex'
                    )}
                    additionalOnClick={() =>
                      datalayerHandler('cta', undefined, undefined, {
                        kind: 'link',
                        title: convertReactNodeToString(title),
                        link: secondaryAction.url,
                        label: secondaryAction.title,
                      })
                    }
                  >
                    <span className={getClassName(classes, 'clickableLabelClassName', '')}>
                      {htmlDecode(secondaryAction.title)}
                    </span>
                    {secondaryAction.SVGIcon && (
                      <svg
                        className={getClassName(classes, 'clickableIconClassName', 'ml-3')}
                        aria-hidden="true"
                        width={secondaryAction.SVGIcon.width ? secondaryAction.SVGIcon.width : '24px'}
                        height={secondaryAction.SVGIcon.height ? secondaryAction.SVGIcon.height : '24px'}
                      >
                        <use xlinkHref={getClassName(classes, 'iconSecondaryId', '#icon--chevron-right-large')} />
                      </svg>
                    )}
                  </Link>
                </div>
              )}
            </footer>
          )}
        </div>

        {image && (
          <div className={getClassName(classes, 'visualClassName', 'flex-grow-0')}>
            <Image
              {...image}
              classNames={{
                image: getClassName(
                  classes,
                  'imageClassName',
                  `${displayOption !== 'content-width' ? 'd-block ml-auto' : ''}`
                ),
                picture: getClassName(classes, 'pictureClassName', ''),
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}
